import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { CostLine, OnderhoudsFactuur } from '@fleet/shared';
import { catToBucket, euro, fmtDate } from '@fleet/shared';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { Panel } from '../../components/ui/Panel';
import { invoiceDocumentUrl, useDeleteInvoice } from '../invoices/invoices.api';

/**
 * Maintenance history for a car: every booked onderhoudsfactuur as a row with
 * date, what was done, garage and amount, newest first, with a total.
 *
 * The whole row is the link to the stored document, not just the amount, so it is
 * an easy target on a phone too. Rows whose invoice has no stored document are not
 * links; imports from before the documents were kept in R2 have nothing to open.
 *
 * The bin removes the invoice everywhere, here and on the Facturen tab, because it
 * is one and the same record. Only for ONDERHOUD invoices: an MTC import books on
 * twenty cars at once and deleting that from one car's page would take the whole
 * import with it.
 */
export function OnderhoudLog({
  costLines,
  facturen = [],
}: {
  costLines: CostLine[];
  facturen?: readonly OnderhoudsFactuur[];
}) {
  const [teVerwijderen, setTeVerwijderen] = useState<OnderhoudsFactuur | null>(null);
  const [fout, setFout] = useState('');
  const del = useDeleteInvoice();

  const items = costLines
    .filter((c) => catToBucket(c.categorie) === 'onderhoud')
    .sort((a, b) => ((a.datum ?? '') < (b.datum ?? '') ? 1 : -1));
  const totaal = items.reduce((s, r) => s + r.bedragExcl, 0);
  const perId = new Map(facturen.map((f) => [f.id, f]));

  function verwijder() {
    if (!teVerwijderen) return;
    setFout('');
    del.mutate(teVerwijderen.id, {
      onSuccess: () => setTeVerwijderen(null),
      onError: (e: unknown) => setFout(e instanceof Error ? e.message : 'Verwijderen mislukt'),
    });
  }

  return (
    <>
      <Panel style={{ padding: '20px 24px' }}>
        {items.length === 0 ? (
          <div style={{ fontSize: 13, color: '#888888' }}>
            Nog geen onderhoudsfacturen. Upload er een via de Facturen-tab, of voeg een beurt toe
            met de plusknop op de onderhoudskaart.
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 12,
                fontSize: 12.5,
                color: '#888888',
                paddingBottom: 10,
              }}
            >
              <span>
                {items.length} {items.length === 1 ? 'factuur' : 'facturen'} · excl. btw
              </span>
              <span style={{ fontSize: 15, fontWeight: 900, color: '#111111' }}>
                {euro(totaal)}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {items.map((r) => (
                <Regel
                  key={r.id}
                  regel={r}
                  factuur={perId.get(r.invoiceId)}
                  onVerwijder={() => {
                    setFout('');
                    setTeVerwijderen(perId.get(r.invoiceId) ?? null);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </Panel>

      {teVerwijderen && (
        <ConfirmModal
          title="Factuur verwijderen"
          message={beschrijfGevolg(teVerwijderen)}
          confirmLabel="Verwijderen"
          danger
          busy={del.isPending}
          error={fout}
          onConfirm={verwijder}
          onClose={() => {
            setFout('');
            setTeVerwijderen(null);
          }}
        />
      )}
    </>
  );
}

/** Spell out what goes, so a shared invoice cannot be deleted by surprise. */
function beschrijfGevolg(f: OnderhoudsFactuur): string {
  const naam = f.factuurnummer ? `Factuur ${f.factuurnummer}` : 'Deze factuur';
  const datum = f.datum ? ` van ${fmtDate(f.datum)}` : '';
  const regels = `${f.aantalRegels} ${f.aantalRegels === 1 ? 'kostenregel' : 'kostenregels'}`;
  const autos =
    f.kentekens.length > 1 ? ` op ${f.kentekens.length} auto's (${f.kentekens.join(', ')})` : '';
  const doc = f.metDocument ? ' Het bewaarde document gaat er ook af.' : '';
  return `${naam}${datum} wordt verwijderd, met ${regels}${autos}. Dat geldt ook op de Facturen-tab, en het laatste onderhoud van deze auto wordt opnieuw bepaald.${doc} Dit kun je niet ongedaan maken.`;
}

function Regel({
  regel,
  factuur,
  onVerwijder,
}: {
  regel: CostLine;
  factuur: OnderhoudsFactuur | undefined;
  onVerwijder: () => void;
}) {
  const [hover, setHover] = useState(false);
  const [binHover, setBinHover] = useState(false);
  const link = factuur?.metDocument ? invoiceDocumentUrl(factuur.id) : null;
  const magWeg = factuur?.type === 'ONDERHOUD';

  const inhoud = (
    <>
      <span
        style={{
          color: '#888888',
          fontFamily: "'Roboto Mono', monospace",
          fontSize: 12,
          flex: 'none',
          width: 92,
        }}
      >
        {regel.datum ? fmtDate(regel.datum) : '—'}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 700, color: '#111111' }}>
          {regel.omschrijving || 'Onderhoudsbeurt'}
        </span>
        {regel.station && <span style={{ color: '#666666' }}> · {regel.station}</span>}
      </span>
      <span style={{ fontWeight: 700, flex: 'none', color: link ? '#2A6085' : '#111111' }}>
        {euro(regel.bedragExcl)}
      </span>
    </>
  );

  const rij: CSSProperties = {
    display: 'flex',
    alignItems: 'baseline',
    gap: 10,
    flex: 1,
    minWidth: 0,
    padding: '10px 6px',
    fontSize: 13,
    textDecoration: 'none',
    color: 'inherit',
    borderRadius: 2,
    background: hover && link ? '#F7F2EA' : 'transparent',
    transition: 'background .15s ease',
  };

  return (
    <div
      // Center, niet baseline: een grid-knop heeft geen echte tekstbaseline, dus die
      // verzint er een en dan hangt het icoon bovenaan de regel.
      style={{ display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #F0E8DC' }}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
    >
      {link ? (
        <a href={link} target="_blank" rel="noreferrer" style={rij} title="Factuur openen">
          {inhoud}
        </a>
      ) : (
        <div style={{ ...rij, cursor: 'default' }} title="Geen document bewaard">
          {inhoud}
        </div>
      )}
      {magWeg && (
        <button
          type="button"
          onClick={onVerwijder}
          aria-label="Factuur verwijderen"
          title="Factuur verwijderen"
          onPointerEnter={() => setBinHover(true)}
          onPointerLeave={() => setBinHover(false)}
          style={{
            flex: 'none',
            width: 30,
            height: 30,
            boxSizing: 'border-box',
            padding: 0,
            lineHeight: 0,
            display: 'grid',
            placeItems: 'center',
            // Alleen rood als je op de knop zelf staat; rood worden zodra je ergens
            // op de regel hovert leest als een waarschuwing die er niet is.
            border: `1px solid ${binHover ? '#B0543B' : 'transparent'}`,
            borderRadius: 2,
            background: binHover ? '#FBF1EE' : 'transparent',
            color: binHover ? '#B0543B' : hover ? '#A2917B' : '#C9BBA8',
            cursor: 'pointer',
            transition: 'color .15s ease, border-color .15s ease, background .15s ease',
          }}
        >
          <PrullenbakIcoon />
        </button>
      )}
    </div>
  );
}

function PrullenbakIcoon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{ display: 'block' }}
    >
      <path
        d="M4 7h16M10 4h4M6 7l1 13h10l1-13M10 11v6M14 11v6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
